/**
 * AdminStridesRulesPage — single screen to edit the entire Strides economy.
 *
 * Two sections:
 *  1. Earn rules — every action a user can earn Strides for. Editable value,
 *     daily cap, dedupe window, enabled toggle, label.
 *  2. Redemption tiers — Strides cost ↔ dollar discount mapping.
 *
 * No deploy needed to tune values: writes hit MongoDB via the admin endpoints
 * and the backend reads from the DB on every request.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Sparkles, Plus, Trash2, RefreshCw, Info, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// System-only rules the editor hides — they aren't traveler-facing earns.
const HIDDEN_RULES = new Set([
  'admin_grant',
  'admin_revoke',
  'peer_transfer',
  'purchase_cosmetic',
  'redeem_tour_discount',
]);

const cents = (n) => `$${(Number(n || 0) / 100).toFixed(2)}`;

const AdminStridesRulesPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [savingRules, setSavingRules] = useState(false);
  const [savingTiers, setSavingTiers] = useState(false);
  const [rules, setRules] = useState({});
  const [tiers, setTiers] = useState([]); // ordered list

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [rulesRes, tiersRes] = await Promise.all([
        axios.get(`${API}/admin/strides/rules`, { headers }),
        axios.get(`${API}/admin/strides/discount-tiers`, { headers }),
      ]);
      setRules(rulesRes.data?.rules || {});
      setTiers(tiersRes.data?.tiers || []);
    } catch (err) {
      toast.error('Failed to load Strides config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const [rulesRes, tiersRes] = await Promise.all([
          axios.get(`${API}/admin/strides/rules`, { headers }),
          axios.get(`${API}/admin/strides/discount-tiers`, { headers }),
        ]);
        if (cancelled) return;
        setRules(rulesRes.data?.rules || {});
        setTiers(tiersRes.data?.tiers || []);
      } catch (err) {
        if (!cancelled) toast.error('Failed to load Strides config');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---------------- Earn rules ----------------
  const visibleRuleKeys = useMemo(
    () => Object.keys(rules).filter((k) => !HIDDEN_RULES.has(k)),
    [rules]
  );

  const updateRuleField = (key, field, value) => {
    setRules((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: value },
    }));
  };

  const saveRules = async () => {
    setSavingRules(true);
    try {
      // Only send the user-facing rules. Backend merges; system rules untouched.
      const payload = Object.fromEntries(
        visibleRuleKeys.map((k) => [
          k,
          {
            value: Number(rules[k]?.value || 0),
            daily_cap:
              rules[k]?.daily_cap === '' || rules[k]?.daily_cap == null
                ? null
                : Number(rules[k].daily_cap),
            dedupe_window_seconds: Number(rules[k]?.dedupe_window_seconds || 0),
            enabled: rules[k]?.enabled !== false,
            label: rules[k]?.label || '',
          },
        ])
      );
      const res = await axios.patch(`${API}/admin/strides/rules`, payload, { headers });
      setRules(res.data?.rules || rules);
      toast.success('Earn rules saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save rules');
    } finally {
      setSavingRules(false);
    }
  };

  // ---------------- Redemption tiers ----------------
  const updateTierField = (idx, field, value) => {
    setTiers((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const removeTier = (idx) => {
    setTiers((prev) => prev.filter((_, i) => i !== idx));
  };

  const addTier = () => {
    const newKey = `tier_${Date.now().toString(36)}`;
    setTiers((prev) => [
      ...prev,
      {
        tier_key: newKey,
        strides_cost: 200,
        discount_usd_cents: 100,
        label: '$1 off',
        description: '',
      },
    ]);
  };

  const saveTiers = async () => {
    // Front-end validation — reject empty / duplicate / invalid entries.
    const keys = new Set();
    for (const t of tiers) {
      if (!t.tier_key || !t.tier_key.trim()) {
        toast.error('Every tier needs a key');
        return;
      }
      if (keys.has(t.tier_key.trim())) {
        toast.error(`Duplicate tier key: ${t.tier_key}`);
        return;
      }
      keys.add(t.tier_key.trim());
      if (!Number(t.strides_cost) || !Number(t.discount_usd_cents)) {
        toast.error(`Tier "${t.tier_key}" needs a positive Strides cost and dollar value`);
        return;
      }
    }

    setSavingTiers(true);
    try {
      const body = {
        tiers: tiers.map((t) => ({
          tier_key: t.tier_key.trim(),
          strides_cost: Number(t.strides_cost),
          discount_usd_cents: Number(t.discount_usd_cents),
          label: t.label || `$${(Number(t.discount_usd_cents) / 100).toFixed(0)} off`,
          description: t.description || '',
        })),
      };
      const res = await axios.put(`${API}/admin/strides/discount-tiers`, body, { headers });
      // Server returns dict — convert to ordered list
      const dict = res.data?.tiers || {};
      const ordered = Object.entries(dict)
        .map(([k, v]) => ({ tier_key: k, ...v }))
        .sort((a, b) => (a.strides_cost || 0) - (b.strides_cost || 0));
      setTiers(ordered);
      toast.success('Redemption tiers saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save tiers');
    } finally {
      setSavingTiers(false);
    }
  };

  // ---------------- Render ----------------
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/admin')}
            data-testid="back-to-admin"
            className="inline-flex items-center gap-2 text-slate-700 hover:text-slate-900 font-semibold"
          >
            <ArrowLeft className="w-4 h-4" />
            Admin
          </button>
          <div className="flex items-center gap-2 text-slate-600">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <span className="text-sm font-bold">Strides Economy</span>
          </div>
          <button
            onClick={fetchAll}
            disabled={loading}
            data-testid="refresh-strides-config"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm font-semibold disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-10">
        {/* Intro note */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <p className="font-bold mb-1">Calibrate the Strides economy here</p>
            <p>
              Set 1 Stride to roughly half a cent so the dollar values stay sane. Changes save to the
              database immediately — no deploy needed. Existing user balances are untouched.
            </p>
          </div>
        </div>

        {/* SECTION 1 — Earn rules */}
        <section data-testid="earn-rules-section">
          <div className="flex items-end justify-between gap-3 mb-4">
            <div>
              <h2 className="text-2xl font-black text-slate-900">Earn rules</h2>
              <p className="text-sm text-slate-600">What every traveler-facing action is worth.</p>
            </div>
            <button
              onClick={saveRules}
              disabled={savingRules || loading}
              data-testid="save-earn-rules"
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-full px-5 py-2.5 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {savingRules ? 'Saving…' : 'Save earn rules'}
            </button>
          </div>

          {loading && Object.keys(rules).length === 0 ? (
            <p className="text-slate-500 text-sm">Loading…</p>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider text-[11px]">
                  <tr>
                    <th className="px-4 py-3 text-left">Action</th>
                    <th className="px-4 py-3 text-left">Label (shown to users)</th>
                    <th className="px-4 py-3 text-right">Strides</th>
                    <th className="px-4 py-3 text-right">Daily cap</th>
                    <th className="px-4 py-3 text-right">Cool-down (sec)</th>
                    <th className="px-4 py-3 text-center">Enabled</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {visibleRuleKeys.map((key) => {
                    const rule = rules[key] || {};
                    return (
                      <tr key={key} data-testid={`rule-row-${key}`}>
                        <td className="px-4 py-3 font-mono text-xs text-slate-500">{key}</td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={rule.label || ''}
                            onChange={(e) => updateRuleField(key, 'label', e.target.value)}
                            data-testid={`rule-label-${key}`}
                            className="w-full px-2 py-1.5 border border-slate-200 rounded focus:outline-none focus:border-slate-400 text-sm"
                            placeholder="Description for users"
                          />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <input
                            type="number"
                            min={0}
                            value={rule.value ?? 0}
                            onChange={(e) => updateRuleField(key, 'value', e.target.value)}
                            data-testid={`rule-value-${key}`}
                            className="w-24 px-2 py-1.5 border border-slate-200 rounded focus:outline-none focus:border-slate-400 text-sm text-right"
                          />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <input
                            type="number"
                            min={0}
                            value={rule.daily_cap == null ? '' : rule.daily_cap}
                            onChange={(e) =>
                              updateRuleField(
                                key,
                                'daily_cap',
                                e.target.value === '' ? null : e.target.value
                              )
                            }
                            placeholder="—"
                            data-testid={`rule-daily-cap-${key}`}
                            className="w-20 px-2 py-1.5 border border-slate-200 rounded focus:outline-none focus:border-slate-400 text-sm text-right"
                          />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <input
                            type="number"
                            min={0}
                            value={rule.dedupe_window_seconds ?? 0}
                            onChange={(e) =>
                              updateRuleField(key, 'dedupe_window_seconds', e.target.value)
                            }
                            data-testid={`rule-cooldown-${key}`}
                            className="w-24 px-2 py-1.5 border border-slate-200 rounded focus:outline-none focus:border-slate-400 text-sm text-right"
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={rule.enabled !== false}
                            onChange={(e) => updateRuleField(key, 'enabled', e.target.checked)}
                            data-testid={`rule-enabled-${key}`}
                            className="w-4 h-4 accent-slate-900"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[11px] text-slate-500 mt-3 px-1">
            <strong>Daily cap</strong>: leave empty for unlimited. <strong>Cool-down</strong>: minimum seconds between
            two awards for the same action (prevents farming). 0 = no cool-down.
          </p>
        </section>

        {/* SECTION 2 — Redemption tiers */}
        <section data-testid="redemption-tiers-section">
          <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
            <div>
              <h2 className="text-2xl font-black text-slate-900">Redemption tiers</h2>
              <p className="text-sm text-slate-600">
                What travelers can spend Strides on. Keep the value-per-Stride consistent.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={addTier}
                data-testid="add-tier"
                className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-bold rounded-full px-4 py-2.5 text-sm"
              >
                <Plus className="w-4 h-4" />
                Add tier
              </button>
              <button
                onClick={saveTiers}
                disabled={savingTiers || loading}
                data-testid="save-tiers"
                className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-full px-5 py-2.5 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {savingTiers ? 'Saving…' : 'Save tiers'}
              </button>
            </div>
          </div>

          {loading && tiers.length === 0 ? (
            <p className="text-slate-500 text-sm">Loading…</p>
          ) : tiers.length === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-8 text-center">
              <p className="text-slate-500 mb-3">No redemption tiers yet.</p>
              <button
                onClick={addTier}
                className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-full px-5 py-2.5 text-sm"
              >
                <Plus className="w-4 h-4" />
                Add your first tier
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tiers.map((tier, idx) => {
                const valuePerStride =
                  Number(tier.discount_usd_cents) /
                  Math.max(Number(tier.strides_cost) || 1, 1);
                return (
                  <div
                    key={tier.tier_key || idx}
                    data-testid={`tier-card-${idx}`}
                    className="bg-white border border-slate-200 rounded-2xl p-5"
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <input
                        type="text"
                        value={tier.tier_key || ''}
                        onChange={(e) => updateTierField(idx, 'tier_key', e.target.value)}
                        data-testid={`tier-key-${idx}`}
                        placeholder="tier_key"
                        className="font-mono text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded px-2 py-1 w-32 focus:outline-none focus:border-slate-400"
                      />
                      <button
                        onClick={() => removeTier(idx)}
                        data-testid={`remove-tier-${idx}`}
                        className="text-rose-500 hover:text-rose-700"
                        title="Remove tier"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1">
                          Label
                        </label>
                        <input
                          type="text"
                          value={tier.label || ''}
                          onChange={(e) => updateTierField(idx, 'label', e.target.value)}
                          data-testid={`tier-label-${idx}`}
                          className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:border-slate-400"
                          placeholder="$3 off"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1">
                            Strides cost
                          </label>
                          <input
                            type="number"
                            min={1}
                            value={tier.strides_cost ?? ''}
                            onChange={(e) => updateTierField(idx, 'strides_cost', e.target.value)}
                            data-testid={`tier-cost-${idx}`}
                            className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:border-slate-400"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1">
                            Discount (¢)
                          </label>
                          <input
                            type="number"
                            min={1}
                            value={tier.discount_usd_cents ?? ''}
                            onChange={(e) =>
                              updateTierField(idx, 'discount_usd_cents', e.target.value)
                            }
                            data-testid={`tier-cents-${idx}`}
                            className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:border-slate-400"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1">
                          Description
                        </label>
                        <input
                          type="text"
                          value={tier.description || ''}
                          onChange={(e) => updateTierField(idx, 'description', e.target.value)}
                          data-testid={`tier-desc-${idx}`}
                          className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:border-slate-400"
                          placeholder="Use 200 Strides for $1 off any tour"
                        />
                      </div>

                      <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs">
                        <span className="text-slate-500 flex items-center gap-1">
                          <DollarSign className="w-3 h-3" /> {cents(tier.discount_usd_cents)} for{' '}
                          {Number(tier.strides_cost || 0).toLocaleString()} Strides
                        </span>
                        <span className="font-bold text-slate-700" data-testid={`tier-per-stride-${idx}`}>
                          {valuePerStride.toFixed(2)}¢ / Stride
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default AdminStridesRulesPage;
